import { Message } from '~/components/component-library'
import { InsufficientGasWarning } from '~/components/smart-accounts/InsufficientGasWarning'
import { CostDisplay } from '~/components/txn-display/cost-display/CostDisplay'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useGasEstimates } from '~/hooks/transactions/useGasEstimates'
import { useGasPaidByLabel } from '~/hooks/transactions/useGasPaidByLabel'
import { useTransactionCost } from '~/hooks/transactions/useTransactionCost'
import { BackendTransactionDecodedCall, BasicTransactionType } from '~/types'

export const TransactionDetailsPreview = ({
  chainId,
  smartAccountId,
  calls,
  transactionTypes,
  showInsufficientGasWarning = false,
}: {
  chainId: SupportedChainId
  smartAccountId: string
  calls: BackendTransactionDecodedCall[]
  transactionTypes: BasicTransactionType[]
  showInsufficientGasWarning?: boolean
}) => {
  const { data: gasData, errorMessage } = useGasEstimates(smartAccountId, {
    chainId,
    calls,
    transactionTypes,
  })

  const transactionCost = useTransactionCost({
    isMerkleTransaction: false,
    chainId,
    gasEstimates: gasData?.gasEstimates,
  })
  const paidBy = useGasPaidByLabel({
    gasPaidBy: gasData?.gasPaidBy,
    smartAccountId,
  })
  const { featureFlags } = useActiveOrg()
  const showUnsponsoredBanner =
    gasData?.gasPaidBy === 'self' && !!featureFlags?.['gas-account']

  return (
    <>
      <CostDisplay
        isLoading={!gasData?.gasEstimates || transactionCost.isLoading}
        isSponsored={!!gasData?.isSponsored}
        fee={{ usd: transactionCost.transactionCost, isEstimate: true }}
        paidBy={paidBy}
      />
      {errorMessage && (
        <div className="mb-3">
          <Message variant="danger">{errorMessage}</Message>
        </div>
      )}
      {showUnsponsoredBanner && (
        <div className="mb-3">
          <Message
            variant="warning"
            title="This transaction is not sponsored"
            action={{
              label: 'Top up',
              variant: 'link',
              href: { path: '/settings/team/gas' },
            }}
          >
            Your account pays gas for this transaction. Top up your gas balance
            so future transactions run without gas tokens.
          </Message>
        </div>
      )}
      {showInsufficientGasWarning && gasData && (
        <InsufficientGasWarning
          smartAccountId={smartAccountId}
          chainId={chainId}
          transactionTypes={transactionTypes}
          transactionCost={transactionCost}
          isSponsored={!!gasData.isSponsored}
        />
      )}
    </>
  )
}
